import React, { useState } from "react";
import { useGamepad } from "./useGamepad";
import { useKeyboard } from "./useKeyboard";
import _, { get } from "lodash";

/**
 * @typedef {object} SetObj
 * @property {number} point - Point du set
 * @property {boolean} win - Indicateur de victoire du set
 */

/**
 * @typedef {object} MatchObj
 * @property {boolean} win - Indicateur de victoire du match
 * @property {Array<SetObj>} sets - Liste des sets du match
 */

/**
 * @typedef {object} TeamScoreObj
 * @property {string} team - Nom de l'équipe
 * @property {Array<MatchObj>} matchs - Liste des matchs de l'équipe
 */

/**
 * @typedef {object} HistoricScoreObj
 * @property {Array<TeamScoreObj>} scores - Scores des équipes
 * @property {number} set - Set courant
 * @property {number} match - Match courant
 * @property {string} servTeam - Équipe au service
 */

/**
 * @typedef {import('../controllers/RpaResourceController').RpaKeyAndPadCenterObj } RpaKeyAndPadCenterObj
 */

/**
 * @typedef {object} RpaScoringBadmintonParams
 * @property {RpaKeyAndPadCenterObj} keyAndPadCenterObj - Objet des keycodes et boutons du pad central
 */

/**
 * @param {RpaScoringBadmintonParams} props
 */
export default function RpaScoringBadminton({ keyAndPadCenterObj }) {
  const [getCurrentSet, setCurrentSet] = useState(0);
  const [getCurrentMatch, setCurrentMatch] = useState(0);
  const [getCurrentServTeam, setCurrentServTeam] = useState("a");
  const [getScores, setScores] = useState(
    /** @type {Array<TeamScoreObj>} */ ([
      {
        team: "a",
        matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
      },
      {
        team: "b",
        matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
      }
    ])
  );
  const [getHistoricScores, setHistoricScores] = useState(
    /** @type {Array<HistoricScoreObj>} */ ([
      {
        scores: [
          {
            team: "a",
            matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
          },
          {
            team: "b",
            matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
          }
        ],
        set: 0,
        match: 0,
        servTeam: "a"
      }
    ])
  );

  useGamepad(buttonIndex => {
    if (buttonIndex === keyAndPadCenterObj.padCodes.addPointTeamA) _onClickOnAddPoint("a");
    if (buttonIndex === keyAndPadCenterObj.padCodes.addPointTeamB) _onClickOnAddPoint("b");
    if (buttonIndex === keyAndPadCenterObj.padCodes.backScoring) _onClickOnBackPoint();
    if (buttonIndex === keyAndPadCenterObj.padCodes.resetScoring) _clear();
  });

  useKeyboard({
    [keyAndPadCenterObj.keyCodes.addPointTeamA]: () => _onClickOnAddPoint("a"),
    [keyAndPadCenterObj.keyCodes.addPointTeamB]: () => _onClickOnAddPoint("b"),
    [keyAndPadCenterObj.keyCodes.resetScoring]: () => _clear(),
    [keyAndPadCenterObj.keyCodes.backScoring]: () => _onClickOnBackPoint()
  });

  /**
   * Foncion de remise à zéro des scores
   * @private
   */
  function _clear() {
    setHistoricScores([
      {
        scores: [
          {
            team: "a",
            matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
          },
          {
            team: "b",
            matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
          }
        ],
        set: 0,
        match: 0,
        servTeam: "a"
      }
    ]);
    setScores([
      {
        team: "a",
        matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
      },
      {
        team: "b",
        matchs: [{ win: false, sets: [{ point: 0, win: false }] }]
      }
    ]);
    setCurrentSet(0);
    setCurrentMatch(0);
    setCurrentServTeam("a");
  }

  /**
   * Gestion du clic sur l'ajout d'un point dans l'historique des scores
   * @param {string} teamStr - Chaîne du team
   * @private
   */
  function _onClickOnAddPoint(teamStr) {
    var oppositTeamPoint = /** @type {TeamScoreObj} */ (getScores.find(score => score.team !== teamStr)).matchs[getCurrentMatch].sets[getCurrentSet].point;

    var scores = getScores.map(score => {
      var sets = score.matchs[getCurrentMatch].sets,
        set = sets[getCurrentSet],
        teamAPoint = set.point;

      // NOTE on incrémente le point du set courant de l'équipe concernée
      if (score.team === teamStr) {
        set = _addPointOnSet(set);

        // NOTE on change l'équipe au service
        setCurrentServTeam(teamStr);

        // NOTE si sur le set courant on se trouve à 21 points, on rajoute un nouveau set
        if (set.point >= 21) {
          if (set.point === 30) {
            set = _winSet(set);
          } else if (set.point - oppositTeamPoint >= 2) {
            set = _winSet(set);
          }
        }

        // NOTE si l'équipe a gagné 2 sets, on rajoute un nouveau match, on change le set et match courant
        if (_teamHasWodTwoSets()) {
          score = _winMatch(score);
        }
      }

      return score;
    });

    setScores(scores);
    setHistoricScores([
      ..._.cloneDeep(getHistoricScores),
      {
        scores: _.cloneDeep(scores),
        set: getCurrentSet,
        match: getCurrentMatch,
        servTeam: getCurrentServTeam
      }
    ]);
  }

  /**
   * Gestion du clic sur le retour en arrière dans l'historique des scores
   * @private
   */
  function _onClickOnBackPoint() {
    if (getHistoricScores.length > 1) {
      var historicScores = _.cloneDeep(getHistoricScores);
      historicScores.pop();
      var previousScores = _.cloneDeep(historicScores[historicScores.length - 1].scores);
      setHistoricScores(historicScores);
      setScores(previousScores);
      setCurrentSet(historicScores[historicScores.length - 1].set);
      setCurrentMatch(historicScores[historicScores.length - 1].match);
      setCurrentServTeam(historicScores[historicScores.length - 1].servTeam);
    }
  }

  /**
   * Permet d'ajouter un point au set donné
   * @param {SetObj} set - Set auquel on ajoute un point
   * @returns {SetObj}
   * @private
   */
  function _addPointOnSet(set) {
    set.point = set.point + 1;
    return set;
  }

  /**
   * Permet de marquer la victoire du set donné
   * @param {SetObj} set - Set auquel on marque la victoire
   * @returns {SetObj}
   * @private
   */
  function _winSet(set) {
    var currentMatch = getCurrentMatch;

    set.win = true;

    setScores(
      getScores.map(score => {
        score.matchs[currentMatch].sets.push({ point: 0, win: false });
        return score;
      })
    );

    setCurrentSet(getCurrentSet + 1);

    return set;
  }

  /**
   * Permet de marquer la victoire du match donné
   * @param {TeamScoreObj} teamScore - Équipe auquel on marque la victoire du match
   * @returns {TeamScoreObj}
   * @private
   */
  function _winMatch(teamScore) {
    var currentMatch = getCurrentMatch;

    teamScore.matchs[currentMatch].win = true;

    setScores(
      getScores.map(score => {
        score.matchs.push({ win: false, sets: [{ point: 0, win: false }] });
        return score;
      })
    );

    setCurrentSet(0);

    setCurrentMatch(getCurrentMatch + 1);

    return teamScore;
  }

  /**
   * Permet de vérifier si une équipe a gagné deux sets
   * @returns {boolean} Si un team a gagné deux sets
   */
  function _teamHasWodTwoSets() {
    var currentMatch = getCurrentMatch,
      teamScoreA = _findTeamScore("a"),
      teamScoreB = _findTeamScore("b"),
      winningSetsA = teamScoreA.matchs[currentMatch].sets.filter(item => item.win).length,
      winningSetsB = teamScoreB.matchs[currentMatch].sets.filter(item => item.win).length;

    return winningSetsA >= 2 || winningSetsB >= 2;
  }

  /**
   * Permet de trouver le score de l'équipe donnée
   * @param {string} teamStr - Nom de l'équipe
   * @returns {TeamScoreObj}
   * @private
   */
  function _findTeamScore(teamStr) {
    var scores = getScores;
    return /** @type {TeamScoreObj} */ (scores.find(score => score.team === teamStr));
  }

  /**
   * Rendu du point de l'équipe pour le set donné
   * @param {string} teamStr - Nom de l'équipe
   * @returns {JSX.Element} - Élément JSX du point de l'équipe pour le set donné
   */
  function renderTeamPoint(teamStr) {
    var scores = getScores,
      teamScore = /** @type {TeamScoreObj} */ (scores.find(score => score.team === teamStr)),
      currentSet = getCurrentSet,
      currentMatch = getCurrentMatch,
      sets = /** @type {Array<any>} */ (teamScore.matchs[currentMatch].sets),
      findedSet = sets[currentSet],
      winningSets = sets.filter(item => item.win).length,
      winningMatchs = teamScore.matchs.filter(match => match.win).length;

    return (
      <div className={`rpascoring__score rpascoring__score--${teamStr}`}>
        {getCurrentServTeam === teamStr && getCurrentSet === currentSet ? <span className="rpascoring__score-serv material-symbols-outlined">badminton</span> : ""}
        <div className="rpascoring__score-wins">
          {Array.from({ length: winningMatchs }).map((_, index) => (
            <span key={index} className="rpascoring__score-win material-symbols-outlined">
              trophy
            </span>
          ))}
        </div>
        <div className="rpascoring__score-point">{findedSet.point}</div>
        <div className="rpascoring__score-set">{winningSets}</div>
      </div>
    );
  }

  /**
   * Rendu du tableau des scores
   * @returns {JSX.Element} - Élément JSX du tableau des scores
   */
  function renderTableScore() {
    return (
      <>
        <div className="rpascoring__scores">
          {renderTeamPoint("a")}
          {renderTeamPoint("b")}
        </div>
      </>
    );
  }

  return (
    <div className="scoringsport">
      <div className="scoringsport__actions">
        <button className="scoringsport__action scoringsport__action--a" onClick={_onClickOnAddPoint.bind(null, "a")}>
          +1
        </button>
        <button className="scoringsport__action" onClick={_onClickOnBackPoint.bind(null)}>
          <span className="material-symbols-outlined">arrow_back</span>
        </button>
        <button className="scoringsport__action scoringsport__action--b" onClick={_onClickOnAddPoint.bind(null, "b")}>
          +1
        </button>
      </div>
      {renderTableScore()}
    </div>
  );
}
